import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Phone, Key, Eye, EyeOff, RefreshCw, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';

// Local storage key for remembered credentials
const SIP_CREDS_KEY = 'connect_sip_creds';

// Simple obfuscation (not encryption, just to avoid plain text storage)
const obfuscate = (str) => btoa(str.split('').reverse().join(''));
const deobfuscate = (str) => {
  try {
    return atob(str).split('').reverse().join('');
  } catch {
    return '';
  }
};

export const getSavedSipCredentials = (phoneNumber) => {
  try {
    const saved = localStorage.getItem(SIP_CREDS_KEY);
    if (!saved) return null;
    const creds = JSON.parse(saved);
    if (creds.phone === phoneNumber) {
      return deobfuscate(creds.password);
    }
  } catch {
    // Invalid data
  }
  return null;
};

export const clearSavedSipCredentials = () => {
  localStorage.removeItem(SIP_CREDS_KEY);
};

export default function SipSetupDialog({ open, onClose, onComplete }) {
  const { user, setupSip } = useAuth();
  const [sipPassword, setSipPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rememberCredentials, setRememberCredentials] = useState(false);
  
  // Check for saved credentials on mount
  useEffect(() => {
    if (open && user?.phone_number) {
      const savedPassword = getSavedSipCredentials(user.phone_number);
      if (savedPassword) {
        setSipPassword(savedPassword);
        setRememberCredentials(true);
      }
    }
  }, [open, user?.phone_number]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!sipPassword.trim()) {
      toast.error('Please enter your SIP password');
      return;
    }

    setSaving(true);
    try {
      await setupSip(sipPassword);
      
      // Save credentials locally if requested
      if (rememberCredentials && user?.phone_number) {
        localStorage.setItem(SIP_CREDS_KEY, JSON.stringify({
          phone: user.phone_number,
          password: obfuscate(sipPassword)
        }));
        toast.success('SIP password configured and saved locally!');
      } else {
        // Clear any saved credentials if "remember" is unchecked
        clearSavedSipCredentials();
        toast.success('SIP password configured! You can now use the softphone.');
      }
      
      onComplete?.();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save SIP password');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    toast.info('You can set up your SIP password later in Profile Settings');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            Set Up Phone Credentials
          </DialogTitle>
          <DialogDescription>
            To use the softphone, please enter your SIP password from your Cloud PBX provider.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-500">SIP Password Required</p>
                <p className="text-muted-foreground text-xs mt-1">
                  This is different from your app login password. It's the password assigned by your PBX administrator for phone registration.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Your Phone Number</Label>
            <Input
              value={user?.phone_number || ''}
              disabled
              className="bg-secondary/30 font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sip-password">SIP Password</Label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="sip-password"
                type={showPassword ? 'text' : 'password'}
                value={sipPassword}
                onChange={(e) => setSipPassword(e.target.value)}
                placeholder="Enter your SIP password"
                className="pl-10 pr-10"
                autoFocus
                data-testid="sip-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Contact your administrator if you don't know your SIP password
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember-credentials"
              checked={rememberCredentials}
              onCheckedChange={setRememberCredentials}
              data-testid="remember-credentials-checkbox"
            />
            <Label 
              htmlFor="remember-credentials" 
              className="text-sm font-normal cursor-pointer flex items-center gap-1.5"
            >
              <Shield className="w-3.5 h-3.5 text-muted-foreground" />
              Remember my credentials on this device
            </Label>
          </div>
          
          {rememberCredentials && (
            <p className="text-xs text-amber-500/80 pl-6">
              Your SIP password will be stored locally in this browser. Only use on personal/trusted devices.
            </p>
          )}
        </form>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleSkip} className="w-full sm:w-auto">
            Skip for Now
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-auto" data-testid="save-sip-btn">
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Key className="w-4 h-4 mr-2" />
            )}
            Save & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
